import { useState, useEffect, useRef, useCallback } from "react";

// ─────────────────────────────────────────────────────────────────────────────
// localStorage keys
// ─────────────────────────────────────────────────────────────────────────────
const LS_MOOD       = "hh_mood";
const LS_HISTORY    = "hh_ex_history";
const LS_DISCLAIMER = "hh_disclaimer_seen";
const LS_STREAK     = "streakCount";

// ─────────────────────────────────────────────────────────────────────────────
// EXERCISE DATA (unchanged)
// ─────────────────────────────────────────────────────────────────────────────
const CATEGORIES = [
  {
    id: "breathing", label: "Breathing", icon: "🫁",
    color: "#6366f1", light: "#eef2ff",
    exercises: [
      {
        id: "box-breathing", emoji: "📦", label: "Box Breathing",
        desc: "The original classic — calm your nervous system in under 2 minutes.",
        science: "Activates the parasympathetic nervous system and lowers cortisol.",
        duration: "2 min", intensity: "Calm",
        steps: [
          { text: "Sit comfortably. Close your eyes and relax your shoulders.", sec: 5 },
          { text: "Inhale slowly through your nose for 4 counts.", sec: 4 },
          { text: "Hold your breath for 4 counts.", sec: 4 },
          { text: "Exhale fully through your mouth for 4 counts.", sec: 4 },
          { text: "Hold empty for 4 counts before the next breath.", sec: 4 },
          { text: "Repeat 3 more times — feel the calm settle in. ✅", sec: 6 },
        ],
      },
      {
        id: "wave-breathing", emoji: "🌊", label: "Wave Breathing",
        desc: "Smooth rhythm designed to melt tension away.",
        science: "Synchronises heart rate variability for optimal parasympathetic tone.",
        duration: "3 min", intensity: "Calm",
        steps: [
          { text: "Inhale gently through the nose for 5 counts.", sec: 5 },
          { text: "Expand chest and belly fully.", sec: 2 },
          { text: "Exhale slowly through the mouth for 6 counts.", sec: 6 },
          { text: "Relax shoulders completely on the exhale.", sec: 3 },
        ],
      },
      {
        id: "478-breathing", emoji: "🔥", label: "4-7-8 Breathing",
        desc: "Navy SEAL technique for rapid stress relief and better sleep.",
        science: "Engages vagal tone — clinically lowers blood pressure within minutes.",
        duration: "4 min", intensity: "Deep",
        steps: [
          { text: "Exhale completely through your mouth — make a whoosh sound.", sec: 4 },
          { text: "Close mouth. Inhale quietly through your nose for 4 counts.", sec: 4 },
          { text: "Hold your breath for 7 counts — stay relaxed.", sec: 7 },
          { text: "Exhale completely through your mouth for 8 counts.", sec: 8 },
          { text: "Repeat the cycle 3 more times. Feel deeply calm. ✅", sec: 5 },
        ],
      },
      {
        id: "cyclic-sighing", emoji: "⚡", label: "Cyclic Sighing",
        desc: "Stanford-researched fastest way to reduce anxiety in real-time.",
        science: "Double inhale re-inflates collapsed alveoli, maximising O₂/CO₂ exchange.",
        duration: "5 min", intensity: "Active",
        steps: [
          { text: "Take a full inhale through your nose.", sec: 3 },
          { text: "Without exhaling — sniff in a second sharp inhale.", sec: 1 },
          { text: "Hold for just a moment.", sec: 2 },
          { text: "Exhale slowly and fully through your mouth.", sec: 8 },
          { text: "Repeat for 5 cycles. Notice the calm. ✅", sec: 5 },
        ],
      },
      {
        id: "ujjayi", emoji: "🌬️", label: "Ocean Breath (Ujjayi)",
        desc: "Yogic breathing that quiets the mind like ocean waves.",
        science: "Slows brainwave activity toward a restful alpha state.",
        duration: "4 min", intensity: "Calm",
        steps: [
          { text: "Inhale through your nose — constrict the throat slightly.", sec: 5 },
          { text: "You should hear a soft ocean-like sound.", sec: 3 },
          { text: "Exhale slowly through the nose or mouth.", sec: 6 },
          { text: "Imagine waves rising on the inhale, receding on the exhale.", sec: 5 },
          { text: "Continue calmly for several rounds. ✅", sec: 10 },
        ],
      },
    ],
  },
  {
    id: "body", label: "Body Relaxation", icon: "🧘",
    color: "#0891b2", light: "#ecfeff",
    exercises: [
      {
        id: "shoulder-release", emoji: "🔄", label: "Shoulder Release",
        desc: "Melt away tension from long hours of sitting or studying.",
        science: "Reduces trapezius hypertonicity — the muscle most linked to anxiety.",
        duration: "3 min", intensity: "Gentle",
        steps: [
          { text: "Sit or stand upright with arms relaxed.", sec: 4 },
          { text: "Slowly roll shoulders backward in big circles × 10.", sec: 10 },
          { text: "Now roll forward × 10.", sec: 10 },
          { text: "Tilt head to the right — hold for 5 seconds.", sec: 5 },
          { text: "Tilt head to the left — hold for 5 seconds.", sec: 5 },
          { text: "Shake it all out. Done! ✅", sec: 4 },
        ],
      },
      {
        id: "grounding-555", emoji: "🌿", label: "5-5-5 Grounding",
        desc: "Instantly anchor yourself when feeling overwhelmed or anxious.",
        science: "Activates the prefrontal cortex — interrupts amygdala-driven anxiety loops.",
        duration: "3 min", intensity: "Calm",
        steps: [
          { text: "Look around. Name 5 things you can SEE.", sec: 10 },
          { text: "Focus. Name 5 things you can HEAR right now.", sec: 10 },
          { text: "Feel your surroundings. Name 5 things you can TOUCH.", sec: 10 },
          { text: "Take a slow deep breath.", sec: 5 },
          { text: "Notice how much calmer you feel. ✅", sec: 5 },
        ],
      },
      {
        id: "power-stretch", emoji: "🤸", label: "Power Stretch",
        desc: "Wake up your body and reset your posture in 60 seconds.",
        science: "Counteracts anterior pelvic tilt from prolonged sitting.",
        duration: "2 min", intensity: "Active",
        steps: [
          { text: "Stand up. Plant feet shoulder-width apart.", sec: 4 },
          { text: "Reach both arms high above your head — stretch tall!", sec: 8 },
          { text: "Lean gently to the right, hold 5 seconds.", sec: 5 },
          { text: "Lean gently to the left, hold 5 seconds.", sec: 5 },
          { text: "Bend forward and let arms hang loose for 6 seconds.", sec: 6 },
          { text: "Roll back up slowly. Roll shoulders back. Done! ✅", sec: 5 },
        ],
      },
      {
        id: "pmr", emoji: "🦋", label: "Progressive Muscle Relaxation",
        desc: "The gold-standard clinical technique — tense and release for deep calm.",
        science: "Jacobson PMR reduces anxiety scores by 48% in clinical trials.",
        duration: "6 min", intensity: "Moderate",
        steps: [
          { text: "Clench both fists tightly for 7 seconds.", sec: 7 },
          { text: "Release completely — notice the warmth and relief.", sec: 10 },
          { text: "Tense your upper arms and biceps — hold.", sec: 7 },
          { text: "Release — feel the profound difference.", sec: 10 },
          { text: "Tense your entire face: squint, clench jaw — hold.", sec: 7 },
          { text: "Release everything. Breathe. ✅", sec: 10 },
        ],
      },
      {
        id: "body-scan", emoji: "🧘", label: "Body Scan Reset",
        desc: "Mindfully visit each body part to find and release tension.",
        science: "MBSR body scan reduces perceived pain by 40% — Kabat-Zinn, UMass.",
        duration: "5 min", intensity: "Gentle",
        steps: [
          { text: "Close your eyes, soften your jaw.", sec: 6 },
          { text: "Relax your feet — release every toe.", sec: 8 },
          { text: "Soften hips, lower back, and abdomen.", sec: 8 },
          { text: "Release your shoulders, arms, and open your hands.", sec: 8 },
          { text: "Relax your face, scalp, and jaw. Take a slow breath. ✅", sec: 10 },
        ],
      },
    ],
  },
  {
    id: "focus", label: "Focus Reset", icon: "🎯",
    color: "#7c3aed", light: "#f5f3ff",
    exercises: [
      {
        id: "focus-anchor", emoji: "🎯", label: "Focus Anchor",
        desc: "Train single-pointed attention to silence mental chatter.",
        science: "Builds dorsolateral prefrontal cortex thickness over time.",
        duration: "5 min", intensity: "Calm",
        steps: [
          { text: "Sit comfortably with a relaxed, upright spine.", sec: 5 },
          { text: "Fix your entire attention on the sensation of breathing.", sec: 10 },
          { text: "When distracted, gently return — no self-judgment.", sec: 8 },
          { text: "Widen awareness to include sounds and sensations. ✅", sec: 10 },
        ],
      },
      {
        id: "thought-labeling", emoji: "🧠", label: "Thought Labeling",
        desc: "Reduce mental noise by naming each thought as it arises.",
        science: "Affect labeling reduces amygdala activation — UCLA fMRI, Lieberman et al.",
        duration: "4 min", intensity: "Gentle",
        steps: [
          { text: "Sit quietly. Observe thoughts without engaging with them.", sec: 8 },
          { text: "When a thought arises, label it: 'planning', 'worry', 'memory'.", sec: 10 },
          { text: "Let the thought float away like a cloud.", sec: 6 },
          { text: "Return to the breath after each label. ✅", sec: 8 },
        ],
      },
      {
        id: "visual-focus", emoji: "🔭", label: "Visual Focus Drill",
        desc: "Use gaze control to instantly sharpen mental concentration.",
        science: "Gaze stabilisation activates the locus coeruleus, boosting norepinephrine.",
        duration: "3 min", intensity: "Active",
        steps: [
          { text: "Pick a single small point on the wall ahead.", sec: 4 },
          { text: "Stare at it — do not let your gaze wander.", sec: 30 },
          { text: "Blink naturally, keep your target locked.", sec: 15 },
          { text: "Take a slow breath — feel your mind quiet down. ✅", sec: 5 },
        ],
      },
      {
        id: "rhythmic-counting", emoji: "🎵", label: "Rhythmic Counting",
        desc: "Ancient breath-counting to entrain brainwaves toward deep focus.",
        science: "Rhythmic attention tasks increase theta-band activity for flow states.",
        duration: "3 min", intensity: "Gentle",
        steps: [
          { text: "Close your eyes. Breathe naturally.", sec: 5 },
          { text: "Count each exhale from 1 to 10, silently and slowly.", sec: 20 },
          { text: "If you lose count, gently restart from 1 — no frustration.", sec: 10 },
          { text: "Open eyes slowly. Notice the increased clarity. ✅", sec: 5 },
        ],
      },
    ],
  },
  {
    id: "energy", label: "Energy Boost", icon: "⚡",
    color: "#d97706", light: "#fffbeb",
    exercises: [
      {
        id: "energy-pulse", emoji: "⚡", label: "Energy Pulse",
        desc: "Quick full-body activation — shake off sluggishness in 2 minutes.",
        science: "Brief sympathetic activation increases alertness for 20-30 minutes.",
        duration: "2 min", intensity: "Active",
        steps: [
          { text: "Stand up — feet wide, arms hanging loose.", sec: 4 },
          { text: "Inhale deeply, raise arms powerfully overhead.", sec: 5 },
          { text: "Exhale forcefully through the mouth, lower arms.", sec: 4 },
          { text: "Shake out your hands and wrists — feel it! ✅", sec: 6 },
        ],
      },
      {
        id: "micro-movement", emoji: "🚶", label: "Micro Movement Reset",
        desc: "2 minutes of movement to refresh body and brain.",
        science: "Increases BDNF (brain-derived neurotrophic factor) — Ratey, Harvard.",
        duration: "2 min", intensity: "Active",
        steps: [
          { text: "Stand up slowly. Shake out your legs.", sec: 5 },
          { text: "Roll shoulders back 10 times with full intention.", sec: 10 },
          { text: "Take 10 mindful steps in any direction.", sec: 12 },
          { text: "Stretch both arms wide — take a huge energising breath. ✅", sec: 8 },
        ],
      },
      {
        id: "kapalabhati", emoji: "🥊", label: "Kapalabhati Breath",
        desc: "Ancient yogic pump breathing for an immediate energy surge.",
        science: "Rapid diaphragm contractions increase cerebral blood flow by up to 14%.",
        duration: "3 min", intensity: "Intense",
        steps: [
          { text: "Sit tall, hands resting on knees.", sec: 4 },
          { text: "Short, sharp exhales through nose — 1 per second × 20.", sec: 20 },
          { text: "Rest — one deep slow inhale and exhale.", sec: 8 },
          { text: "Repeat — 30 pumps. Feel the energising warmth. ✅", sec: 30 },
        ],
      },
      {
        id: "morning-activation", emoji: "🌅", label: "Morning Activation",
        desc: "Full-body sequence to eliminate morning grogginess.",
        science: "Progressive muscle activation raises core temperature to healthy wake levels.",
        duration: "4 min", intensity: "Moderate",
        steps: [
          { text: "Interlace fingers, push arms overhead — big stretch.", sec: 8 },
          { text: "20 jumping jacks or march vigorously on the spot.", sec: 20 },
          { text: "Forward fold — let arms hang loose for 15 seconds.", sec: 15 },
          { text: "Roll up slowly — roll shoulders back. One huge breath. ✅", sec: 8 },
        ],
      },
    ],
  },
  {
    id: "eyes", label: "Eye Care", icon: "👁️",
    color: "#059669", light: "#ecfdf5",
    exercises: [
      {
        id: "20-20-20", emoji: "👁️", label: "20-20-20 Eye Rest",
        desc: "The AAO-recommended rule to prevent digital eye strain.",
        science: "Prevents ciliary muscle fatigue — American Academy of Ophthalmology.",
        duration: "2 min", intensity: "Gentle",
        steps: [
          { text: "Look completely away from your screen.", sec: 3 },
          { text: "Find an object at least 20 feet (6 m) away.", sec: 3 },
          { text: "Focus on that object softly — don't strain.", sec: 20 },
          { text: "Blink slowly 10 times to remoisten your eyes.", sec: 8 },
          { text: "Roll eyes gently in full circles, both directions.", sec: 10 },
          { text: "Eyes refreshed! Set a 20-min reminder. ✅", sec: 5 },
        ],
      },
      {
        id: "palming", emoji: "🌙", label: "Palming Technique",
        desc: "Ancient yogic eye restoration used for centuries.",
        science: "Complete light deprivation enables retinal recovery, reduces intraocular pressure.",
        duration: "3 min", intensity: "Gentle",
        steps: [
          { text: "Rub palms together vigorously for 10 seconds.", sec: 10 },
          { text: "Gently cup warm palms over closed eyes.", sec: 20 },
          { text: "Visualise deep, restful blackness — breathe slowly.", sec: 15 },
          { text: "Lower hands. Blink gently into the light. ✅", sec: 5 },
        ],
      },
      {
        id: "soft-gaze", emoji: "🔵", label: "Soft Gaze (Panoramic Vision)",
        desc: "Instantly reduce stress by switching to wide peripheral vision.",
        science: "Panoramic vision activates the serotonin system, deactivates fight-or-flight.",
        duration: "2 min", intensity: "Gentle",
        steps: [
          { text: "Look straight ahead with a soft, unfocused gaze.", sec: 8 },
          { text: "Without moving your head, expand peripheral vision as wide as possible.", sec: 12 },
          { text: "Hold — notice how your body automatically relaxes.", sec: 10 },
          { text: "Breathe slowly, keep the wide open gaze. ✅", sec: 8 },
        ],
      },
    ],
  },
  {
    id: "mindfulness", label: "Mindfulness", icon: "☀️",
    color: "#be185d", light: "#fdf2f8",
    exercises: [
      {
        id: "metta", emoji: "☀️", label: "Loving-Kindness (Metta)",
        desc: "Cultivate compassion for yourself and others.",
        science: "Increases grey matter in the insula and cingulate cortex — Davidson, UW-Madison.",
        duration: "8 min", intensity: "Deep",
        steps: [
          { text: "Sit comfortably, eyes closed. Breathe naturally.", sec: 10 },
          { text: "Silently repeat: 'May I be happy. May I be healthy. May I be at peace.'", sec: 20 },
          { text: "Extend to someone you love — send the same wishes to them.", sec: 20 },
          { text: "Extend to a neutral person — someone you see but don't know.", sec: 15 },
          { text: "Extend to all beings everywhere. Feel the expansive warmth. ✅", sec: 20 },
        ],
      },
      {
        id: "nsdr", emoji: "🌊", label: "NSDR / Yoga Nidra",
        desc: "Non-Sleep Deep Rest — recovery protocol used by scientists and elite athletes.",
        science: "Increases striatal dopamine by up to 65% — Huberman Lab, Stanford.",
        duration: "10 min", intensity: "Deep",
        steps: [
          { text: "Lie down or sit reclined. Close eyes. Body completely still.", sec: 10 },
          { text: "Breathe slowly — notice heaviness in your limbs.", sec: 15 },
          { text: "Scan from feet to head: observe sensations, don't change them.", sec: 20 },
          { text: "Rest in open awareness — no agenda, no effort.", sec: 30 },
          { text: "Wiggle fingers, take a breath, return gently. ✅", sec: 8 },
        ],
      },
      {
        id: "mindful-observation", emoji: "🌸", label: "Mindful Object Observation",
        desc: "Use any ordinary object to practice total present-moment awareness.",
        science: "Deactivates the default mode network — reduces rumination and mind-wandering.",
        duration: "5 min", intensity: "Calm",
        steps: [
          { text: "Pick any object nearby — a pen, a leaf, a mug.", sec: 5 },
          { text: "Study it as if seeing it for the first time.", sec: 20 },
          { text: "Notice colour, texture, light, shadow, weight.", sec: 15 },
          { text: "Take a breath — notice how present you feel. ✅", sec: 8 },
        ],
      },
      {
        id: "vipassana", emoji: "🔮", label: "Vipassana Micro-Sit",
        desc: "5-minute insight meditation from the Theravada tradition.",
        science: "10 days of Vipassana reduces anxiety scores by 30% — NIH-funded study.",
        duration: "5 min", intensity: "Moderate",
        steps: [
          { text: "Sit still. Notice the breath entering and leaving.", sec: 10 },
          { text: "Observe bodily sensations without reacting to them.", sec: 15 },
          { text: "If a sensation is strong — observe it, watch it change and pass.", sec: 15 },
          { text: "Return to breath. Equanimity is the goal. ✅", sec: 10 },
        ],
      },
    ],
  },
  {
    id: "posture", label: "Posture & Strength", icon: "🏛️",
    color: "#b45309", light: "#fef3c7",
    exercises: [
      {
        id: "wall-angel", emoji: "🏛️", label: "Wall Angel",
        desc: "The #1 physiotherapy exercise to fix forward head posture.",
        science: "Activates lower trapezius — corrects upper-crossed syndrome.",
        duration: "3 min", intensity: "Moderate",
        steps: [
          { text: "Stand with back flat against a wall, feet 6 inches away.", sec: 5 },
          { text: "Press lower back, shoulders, and head firmly to wall.", sec: 5 },
          { text: "Raise arms to 90° goalpost — maintain full wall contact.", sec: 8 },
          { text: "Slowly slide arms up, then down × 10. ✅", sec: 20 },
        ],
      },
      {
        id: "glute-bridge", emoji: "🌉", label: "Glute Bridge Hold",
        desc: "Reactivate dormant glutes and restore your posterior chain.",
        science: "Gluteal amnesia from sitting is reversed in 4 weeks — McGill.",
        duration: "3 min", intensity: "Moderate",
        steps: [
          { text: "Lie on back, feet flat, hip-width apart, arms by sides.", sec: 5 },
          { text: "Press feet into floor, lift hips to form a straight line.", sec: 8 },
          { text: "Squeeze glutes hard at the top — hold 3 seconds.", sec: 6 },
          { text: "Lower slowly. Repeat × 10. ✅", sec: 20 },
        ],
      },
      {
        id: "thoracic-opener", emoji: "🕊️", label: "Thoracic Opener",
        desc: "Undo hours of desk slouching in just 90 seconds.",
        science: "Thoracic extension reverses kyphotic loading on cervical discs.",
        duration: "2 min", intensity: "Gentle",
        steps: [
          { text: "Sit at the edge of your chair, hands behind your head.", sec: 5 },
          { text: "Slowly arch backwards over the chair back.", sec: 8 },
          { text: "Hold at your comfortable end range — breathe into your chest.", sec: 8 },
          { text: "Return slowly. Repeat × 5. ✅", sec: 15 },
        ],
      },
      {
        id: "dead-bug", emoji: "🦴", label: "Dead Bug Core",
        desc: "The safest deep core exercise — used in spinal rehabilitation worldwide.",
        science: "Activates transversus abdominis without spinal flexion — Dr. Stuart McGill.",
        duration: "4 min", intensity: "Moderate",
        steps: [
          { text: "Lie on back, knees at 90°, arms pointing straight up.", sec: 6 },
          { text: "Press your lower back firmly into the floor.", sec: 5 },
          { text: "Slowly lower right arm and left leg — hover above floor.", sec: 8 },
          { text: "Return and alternate sides. Breathe naturally. ✅", sec: 15 },
        ],
      },
      {
        id: "childs-pose", emoji: "🌱", label: "Child's Pose Hold",
        desc: "The ultimate full-body decompression stretch.",
        science: "Reduces lumbar disc pressure and activates the rest-and-digest response.",
        duration: "3 min", intensity: "Gentle",
        steps: [
          { text: "Kneel and sit back toward your heels.", sec: 5 },
          { text: "Reach arms long in front — forehead to the floor.", sec: 5 },
          { text: "Breathe into your lower back — feel it expand.", sec: 20 },
          { text: "Hold for 60 seconds, breathing slowly throughout. ✅", sec: 30 },
        ],
      },
    ],
  },
];

const ALL_EXERCISES = CATEGORIES.flatMap(c =>
  c.exercises.map(ex => ({ ...ex, catId: c.id }))
);

// ─────────────────────────────────────────────────────────────────────────────
// PERSONALIZATION ENGINE (unchanged)
// ─────────────────────────────────────────────────────────────────────────────
const MOOD_CATS = {
  low:  ["breathing", "body", "mindfulness"],
  mid:  ["focus", "mindfulness", "eyes"],
  high: ["energy", "posture", "focus"],
};

function getTimeCats() {
  const h = new Date().getHours();
  if (h >= 5  && h < 11) return ["energy", "breathing"];
  if (h >= 11 && h < 16) return ["focus", "eyes"];
  if (h >= 16 && h < 20) return ["body", "mindfulness"];
  return ["mindfulness", "breathing"];
}

function readHistory() {
  try { return JSON.parse(localStorage.getItem(LS_HISTORY) || "[]"); }
  catch { return []; }
}

function saveToHistory(exId, catId) {
  const prev = readHistory();
  const next = [...prev, { exId, catId, ts: Date.now() }].slice(-20);
  localStorage.setItem(LS_HISTORY, JSON.stringify(next));
}

function scoreExercise(ex, mood, history, streak) {
  let score = 0;
  const moodRange = mood <= 33 ? "low" : mood <= 66 ? "mid" : "high";
  if (MOOD_CATS[moodRange][0] === ex.catId)         score += 30;
  else if (MOOD_CATS[moodRange].includes(ex.catId)) score += 15;
  const tc = getTimeCats();
  if (tc[0] === ex.catId)      score += 25;
  else if (tc[1] === ex.catId) score += 12;
  const recent3 = history.slice(-3).map(h => h.exId);
  const recent6 = history.slice(-6).map(h => h.exId);
  if (recent3.includes(ex.id))       score -= 25;
  else if (recent6.includes(ex.id))  score -= 10;
  else                                score += 25;
  const easy = ["Calm", "Gentle"];
  const hard = ["Deep", "Intense"];
  if (streak === 0 && easy.includes(ex.intensity))   score += 20;
  else if (streak >= 7 && hard.includes(ex.intensity)) score += 20;
  else if (streak >= 1 && streak < 7)                score += 10;
  return score;
}

function getRecommendations(mood, history, streak, count = 3) {
  return [...ALL_EXERCISES]
    .map(ex => ({ ...ex, _score: scoreExercise(ex, mood, history, streak) }))
    .sort((a, b) => b._score - a._score)
    .slice(0, count);
}

// ─────────────────────────────────────────────────────────────────────────────
// INTENSITY colours (unchanged)
// ─────────────────────────────────────────────────────────────────────────────
const INTENSITY = {
  Calm:     { bg: "rgba(21,128,61,0.12)",   fg: "#15803d" },
  Gentle:   { bg: "rgba(29,78,216,0.1)",    fg: "#1d4ed8" },
  Active:   { bg: "rgba(194,65,12,0.1)",    fg: "#c2410c" },
  Moderate: { bg: "rgba(126,34,206,0.1)",   fg: "#7e22ce" },
  Deep:     { bg: "rgba(190,24,93,0.1)",    fg: "#be185d" },
  Intense:  { bg: "rgba(185,28,28,0.1)",    fg: "#b91c1c" },
};

function getMoodDescription(mood) {
  if (mood <= 33) return { label: "Feeling Low",   emoji: "😞", color: "#e11d48" };
  if (mood <= 66) return { label: "Feeling Okay",  emoji: "😐", color: "#d97706" };
  return             { label: "Feeling Great", emoji: "😊", color: "#059669" };
}

// ─────────────────────────────────────────────────────────────────────────────
// SUB-COMPONENTS (unchanged)
// ─────────────────────────────────────────────────────────────────────────────
function DisclaimerBanner({ onDismiss }) {
  return (
    <div className="ex-disclaimer-box">
      <span style={{ fontSize: "1.1rem", flexShrink: 0, marginTop: 1 }}>ℹ️</span>
      <p className="ex-disclaimer-text">
        <strong>Note:</strong> Exercises are based on widely known wellness and mindfulness practices
        from <strong>psychology, neuroscience, and yoga traditions</strong>. They are intended for
        general well-being only and are <strong>not a substitute for professional medical or
        mental health advice</strong>. If you are experiencing a mental health crisis, please
        consult a qualified professional.
      </p>
      <button onClick={onDismiss} aria-label="Dismiss" className="ex-disclaimer-btn">✕</button>
    </div>
  );
}

function MoodContextPill({ mood }) {
  const { label, emoji, color } = getMoodDescription(mood);
  const h = new Date().getHours();
  const timeLabel = h >= 5 && h < 11 ? "Morning" : h >= 11 && h < 16 ? "Afternoon" : h >= 16 && h < 20 ? "Evening" : "Night";
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
      background: "var(--bg-secondary)", border: `1.5px solid ${color}40`,
      borderRadius: 14, padding: "12px 18px", marginBottom: 22,
    }}>
      <span style={{ fontSize: "1.6rem" }}>{emoji}</span>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 700, fontSize: "0.88rem", color, marginBottom: 2 }}>{label}</div>
        <div style={{ fontSize: "0.72rem", color: "var(--text-muted)" }}>
          Your picks are personalised for {timeLabel.toLowerCase()} · Update your mood from the Dashboard
        </div>
      </div>
      <div style={{ display: "flex", gap: 6 }}>
        {["😞","😐","😊"].map((e, i) => {
          const active = (i === 0 && mood <= 33) || (i === 1 && mood > 33 && mood <= 66) || (i === 2 && mood > 66);
          return (
            <div key={i} className="ex-mood-bubble" style={{
              background: active ? color + "20" : "var(--bg-tertiary)",
              border: `2px solid ${active ? color : "var(--border-color)"}`,
            }}>{e}</div>
          );
        })}
      </div>
    </div>
  );
}

function RecommendedCard({ ex, rank, onStart }) {
  const cat = CATEGORIES.find(c => c.id === ex.catId);
  const int = INTENSITY[ex.intensity] || INTENSITY.Gentle;
  const [hov, setHov] = useState(false);
  const rankLabel = ["✨ Top Pick", "⭐ Great Fit", "💡 Try This"][rank] || "💡";
  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      onClick={() => onStart(ex, cat)}
      style={{
        position: "relative", cursor: "pointer", borderRadius: 16,
        border: `2px solid ${hov ? cat.color : cat.color + "55"}`,
        background: `linear-gradient(135deg,${cat.color}${hov ? "18" : "0d"},${cat.light}${hov ? "ff" : "99"})`,
        padding: "16px 18px", paddingTop: "40px",
        transform: hov ? "translateY(-3px)" : "none",
        boxShadow: hov ? `0 12px 28px ${cat.color}28` : `0 2px 8px ${cat.color}12`,
        transition: "all 0.22s ease",
      }}
    >
      <div style={{
        position: "absolute", top: 12, right: 12,
        background: cat.color, color: "#fff",
        fontSize: "0.6rem", fontWeight: 800, borderRadius: 999,
        padding: "3px 10px", letterSpacing: "0.04em", whiteSpace: "nowrap", zIndex: 2,
      }}>{rankLabel}</div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
        <span style={{ fontSize: "1.7rem" }}>{ex.emoji}</span>
        <div>
          <div style={{ fontWeight: 700, fontSize: "0.93rem", color: "var(--text-color)" }}>{ex.label}</div>
          <div style={{ fontSize: "0.7rem", color: cat.color, fontWeight: 600 }}>{cat.label}</div>
        </div>
      </div>
      <p style={{ margin: "0 0 10px", fontSize: "0.78rem", color: "var(--text-muted)", lineHeight: 1.45 }}>{ex.desc}</p>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
          <span style={{ background: int.bg, color: int.fg, padding: "2px 8px", borderRadius: 999, fontSize: "0.63rem", fontWeight: 700 }}>{ex.intensity}</span>
          <span className="ex-dur">⏱ {ex.duration}</span>
        </div>
        <button onClick={e => { e.stopPropagation(); onStart(ex, cat); }} style={{
          background: cat.color, color: "#fff", border: "none", borderRadius: 8,
          padding: "6px 14px", fontSize: "0.76rem", fontWeight: 700, cursor: "pointer",
          fontFamily: "'Plus Jakarta Sans','DM Sans',sans-serif", boxShadow: `0 2px 8px ${cat.color}40`,
          flexShrink: 0,
        }}>Begin →</button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// EXERCISE PLAYER  ── completely rewritten timer engine
// ─────────────────────────────────────────────────────────────────────────────
//
// ROOT CAUSE of original bugs
// ───────────────────────────
// 1. Calling setState inside another setState's updater callback
//    (`setStep` and `setRunning` inside `setTLeft(t => {...})`) is
//    unsupported — React can defer or skip nested updater calls.
//
// 2. `step` captured in the interval's closure becomes stale; once the
//    effect re-runs (due to [running, step] deps) a *new* interval is
//    spawned while the old one may still fire once more — causing a
//    double-tick or a skipped tick at every transition.
//
// 3. A second `useEffect` that resets `tLeft` when `step` changes races
//    against the main timer effect — both run in the same commit, but
//    the timer can fire *before* the reset is visible to React state.
//
// 4. No tab-visibility handling → timer drifts when the tab is hidden.
//
// FIX STRATEGY
// ────────────
// • Single `useRef` ("live") holds the authoritative timer state.
//   The interval callback reads/writes the ref — NEVER closes over
//   React state, so it is always fresh with zero stale-closure risk.
// • React state (`ui`) is updated only for rendering — driven by the ref.
// • All control functions (play/pause/skip/restart) write to the ref
//   first, then schedule the corresponding state update.
// • Step transitions happen *outside* the interval: the interval is
//   stopped, a 600 ms CSS-friendly gap fires via setTimeout, then the
//   next step's interval is started cleanly.
// • `document.visibilitychange` catches up the timer when the tab
//   returns from the background.
// • Web Audio API beeps (silent-fail) give a tactile sense of pacing.
// • Space-bar keyboard shortcut via phaseRef (always fresh).
// ─────────────────────────────────────────────────────────────────────────────

function ExercisePlayer({ ex, cat, onBack, onComplete }) {
  // ── UI state (only for rendering) ────────────────────────────────────────
  const [ui, setUi] = useState({
    stepIdx:  0,
    tLeft:    ex.steps[0].sec,
    phase:    "idle",   // "idle" | "running" | "paused" | "done"
    elapsed:  0,
    busy:     false,    // true during the 600 ms between-step gap
  });

  // ── Authoritative live state (never stale in closures) ───────────────────
  const live = useRef({
    stepIdx: 0,
    tLeft:   ex.steps[0].sec,
    elapsed: 0,
    phase:   "idle",
  });

  const intervalRef = useRef(null);
  const phaseRef    = useRef("idle");   // for keyboard handler (avoids closure capture)

  // fn bag — always re-assigned each render so closures inside are always fresh
  const fn = useRef({});

  // ── Helpers ───────────────────────────────────────────────────────────────
  const stopTimer = useCallback(() => {
    clearInterval(intervalRef.current);
    intervalRef.current = null;
  }, []);

  // Tiny, non-intrusive audio ping (silently ignored if AudioContext blocked)
  const ping = useCallback((freq = 660, dur = 0.12) => {
    try {
      const ctx  = new (window.AudioContext || window.webkitAudioContext)();
      const osc  = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.12, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + dur);
      osc.start();
      osc.stop(ctx.currentTime + dur + 0.05);
    } catch (_) {}
  }, []);

  // ── Always-fresh function bag (re-assigned every render) ─────────────────
  fn.current.startInterval = () => {
    stopTimer();
    intervalRef.current = setInterval(() => {
      // Read from ref — guaranteed fresh, no stale closure
      live.current.tLeft   -= 1;
      live.current.elapsed += 1;
      // Drive rendering
      setUi(u => ({ ...u, tLeft: live.current.tLeft, elapsed: live.current.elapsed }));
      // Step finished?
      if (live.current.tLeft <= 0) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
        fn.current.advance();
      }
    }, 1000);
  };

  fn.current.advance = () => {
    ping(660, 0.15);
    const next = live.current.stepIdx + 1;

    if (next >= ex.steps.length) {
      // ── All steps done ────────────────────────────────────────────────
      ping(880, 0.45);
      live.current.phase = "done";
      phaseRef.current   = "done";
      setUi(u => ({ ...u, phase: "done", tLeft: 0, busy: false }));
      onComplete(ex.id, cat.id);
      return;
    }

    // ── Transition to next step (smooth 600 ms gap) ───────────────────
    setUi(u => ({ ...u, busy: true, tLeft: 0 }));

    setTimeout(() => {
      live.current.stepIdx = next;
      live.current.tLeft   = ex.steps[next].sec;
      setUi(u => ({ ...u, stepIdx: next, tLeft: ex.steps[next].sec, busy: false }));
      // Auto-start the next step immediately
      fn.current.startInterval();
    }, 600);
  };

  // ── Controls ─────────────────────────────────────────────────────────────
  const play = useCallback(() => {
    live.current.phase = "running";
    phaseRef.current   = "running";
    setUi(u => ({ ...u, phase: "running" }));
    fn.current.startInterval();
  }, []);   // stable ref — fn.current is always fresh

  const pause = useCallback(() => {
    stopTimer();
    live.current.phase = "paused";
    phaseRef.current   = "paused";
    setUi(u => ({ ...u, phase: "paused" }));
  }, [stopTimer]);

  const skip = useCallback(() => {
    stopTimer();
    const next = live.current.stepIdx + 1;
    if (next >= ex.steps.length) return;
    live.current.stepIdx = next;
    live.current.tLeft   = ex.steps[next].sec;
    live.current.phase   = "paused";
    phaseRef.current     = "paused";
    setUi(u => ({ ...u, stepIdx: next, tLeft: ex.steps[next].sec, phase: "paused", busy: false }));
  }, [stopTimer, ex.steps]);

  const restart = useCallback(() => {
    stopTimer();
    live.current = { stepIdx: 0, tLeft: ex.steps[0].sec, elapsed: 0, phase: "idle" };
    phaseRef.current = "idle";
    setUi({ stepIdx: 0, tLeft: ex.steps[0].sec, phase: "idle", elapsed: 0, busy: false });
  }, [stopTimer, ex.steps]);

  // ── Space-bar shortcut ────────────────────────────────────────────────────
  useEffect(() => {
    const onKey = (e) => {
      if (e.code !== "Space") return;
      // Don't hijack Space if a button/input has focus
      const tag = document.activeElement?.tagName;
      if (tag === "BUTTON" || tag === "INPUT" || tag === "TEXTAREA") return;
      e.preventDefault();
      const p = phaseRef.current;
      if (p === "running") pause();
      else if (p === "idle" || p === "paused") play();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [play, pause]);

  // ── Tab-visibility catch-up ───────────────────────────────────────────────
  // When a hidden tab returns, advance the timer by the time the tab was gone
  // so the countdown doesn't silently stall behind the user's back.
  useEffect(() => {
    let hiddenAt = null;
    const onVis = () => {
      if (document.hidden) {
        hiddenAt = Date.now();
        return;
      }
      if (!hiddenAt || live.current.phase !== "running") { hiddenAt = null; return; }
      const gapSecs = Math.floor((Date.now() - hiddenAt) / 1000);
      hiddenAt = null;
      const newTLeft = Math.max(0, live.current.tLeft - gapSecs);
      live.current.tLeft = newTLeft;
      setUi(u => ({ ...u, tLeft: newTLeft }));
      if (newTLeft <= 0) {
        stopTimer();
        fn.current.advance();
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [stopTimer]);

  // ── Cleanup on unmount ────────────────────────────────────────────────────
  useEffect(() => () => stopTimer(), [stopTimer]);

  // ── Derived render values ─────────────────────────────────────────────────
  const { stepIdx, tLeft, phase, elapsed, busy } = ui;
  const cur      = ex.steps[stepIdx];
  const nextStep = ex.steps[stepIdx + 1];
  // Progress percentage for the ring (clamped, no NaN)
  const pct = cur && cur.sec > 0
    ? Math.min(100, Math.max(0, ((cur.sec - tLeft) / cur.sec) * 100))
    : busy ? 100 : 0;
  const C = 2 * Math.PI * 52;

  // ── Done screen ───────────────────────────────────────────────────────────
  if (phase === "done") {
    return (
      <div style={{ maxWidth: 420, margin: "0 auto", textAlign: "center", padding: "28px 0" }}>
        <div style={{
          width: 96, height: 96, borderRadius: "50%", margin: "0 auto 20px",
          background: `linear-gradient(135deg, ${cat.color}22, ${cat.color}08)`,
          border: `3px solid ${cat.color}55`,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 48,
          animation: "ep-pop 0.5s cubic-bezier(0.175,0.885,0.32,1.275) both",
        }}>🎉</div>
        <h2 style={{ fontSize: "1.8rem", color: cat.color, margin: "0 0 4px",
          fontFamily: "'Fraunces','DM Serif Display',serif" }}>Complete!</h2>
        <p style={{ color: "var(--text-muted)", marginBottom: 20 }}>{ex.label}</p>
        <div style={{ display: "flex", gap: 16, justifyContent: "center", marginBottom: 20 }}>
          {[["⏱", `${elapsed}s`, "Time"], ["✅", ex.steps.length, "Steps"]].map(([ic, v, l]) => (
            <div key={l} className="ex-done-stat">
              <div style={{ fontSize: 20 }}>{ic}</div>
              <div className="ex-done-stat-val">{v}</div>
              <div className="ex-done-stat-label">{l}</div>
            </div>
          ))}
        </div>
        <div className="ex-done-science">🔬 {ex.science}</div>
        <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
          <button className="ex-btn-primary" style={{ "--c": cat.color }} onClick={restart}>
            Repeat
          </button>
          <button className="ex-btn-ghost" onClick={onBack}>← Back</button>
        </div>
      </div>
    );
  }

  // ── Player screen ─────────────────────────────────────────────────────────
  return (
    <div style={{ maxWidth: 500, margin: "0 auto", display: "flex", flexDirection: "column", gap: 18 }}>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <button className="ex-btn-ghost" onClick={() => { stopTimer(); onBack(); }}>← Back</button>
        <div style={{ fontWeight: 700, color: "var(--text-color)", display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 20 }}>{ex.emoji}</span> {ex.label}
        </div>
        <div className="ex-step-counter">{stepIdx + 1}/{ex.steps.length}</div>
      </div>

      {/* Step progress bars */}
      <div style={{ display: "flex", gap: 4, height: 5 }}>
        {ex.steps.map((_, i) => (
          <div key={i} className="ex-step-progress-bar" style={{
            background: i < stepIdx
              ? cat.color
              : i === stepIdx
                ? busy ? cat.color + "40" : cat.color + "70"
                : "var(--border-color)",
            transition: "background 0.4s ease",
          }} />
        ))}
      </div>

      {/* Timer ring + step text */}
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>

        {/* Circular countdown ring
            key={stepIdx} remounts the SVG on step change so the
            stroke-dashoffset animation resets cleanly — no backward sweep */}
        <div style={{
          position: "relative", width: 130, height: 130,
          display: "flex", alignItems: "center", justifyContent: "center",
          opacity: busy ? 0.55 : 1,
          transition: "opacity 0.3s ease",
        }}>
          <svg key={stepIdx} width={130} height={130}
            style={{ transform: "rotate(-90deg)", position: "absolute" }}>
            <circle cx={65} cy={65} r={52}
              fill="none" stroke="var(--border-color)" strokeWidth={10} />
            <circle cx={65} cy={65} r={52}
              fill="none" stroke={cat.color} strokeWidth={10}
              strokeDasharray={C}
              strokeDashoffset={C * (1 - pct / 100)}
              strokeLinecap="round"
              style={{ transition: busy ? "none" : "stroke-dashoffset 0.95s linear" }} />
          </svg>

          {/* Pulse glow when running */}
          <div style={{
            position: "absolute", inset: 8, borderRadius: "50%",
            boxShadow: phase === "running" && !busy ? `0 0 28px ${cat.color}45` : "none",
            transition: "box-shadow 0.6s ease",
          }} />

          {/* Number display */}
          <div style={{ position: "relative", textAlign: "center" }}>
            {busy ? (
              <>
                <div style={{
                  fontSize: "1.6rem", lineHeight: 1,
                  color: "var(--text-muted)",
                  animation: "ep-dots 0.8s infinite",
                }}>···</div>
                <div className="ex-counter-label" style={{ marginTop: 2 }}>NEXT</div>
              </>
            ) : (
              <>
                <div style={{
                  fontFamily: "'Fraunces','DM Serif Display',serif",
                  fontSize: "2.4rem", color: cat.color, lineHeight: 1,
                  transition: "color 0.3s",
                }}>{tLeft}</div>
                <div className="ex-counter-label">SECONDS</div>
              </>
            )}
          </div>
        </div>

        {/* Step instruction */}
        <div className="ex-player-step-box"
          style={{
            border: `1.5px solid ${cat.color}30`,
            opacity: busy ? 0 : 1,
            transform: busy ? "translateY(4px)" : "translateY(0)",
            transition: "opacity 0.3s ease, transform 0.3s ease",
          }}>
          <p className="ex-player-step-text">{cur?.text}</p>
        </div>
      </div>

      {/* Next-step preview — shown while running so users can mentally prepare */}
      <div style={{
        minHeight: 36,
        opacity: (nextStep && phase === "running" && !busy) ? 1 : 0,
        transform: (nextStep && phase === "running" && !busy) ? "translateY(0)" : "translateY(4px)",
        transition: "opacity 0.4s ease 0.2s, transform 0.4s ease 0.2s",
      }}>
        {nextStep && (
          <div style={{
            display: "flex", alignItems: "center", gap: 8,
            background: "var(--bg-secondary)",
            border: "1px dashed var(--border-color)",
            borderRadius: 10, padding: "8px 14px",
          }}>
            <span style={{ fontSize: "0.65rem", fontWeight: 800, color: cat.color,
              textTransform: "uppercase", letterSpacing: "0.08em", whiteSpace: "nowrap" }}>
              Up next
            </span>
            <span style={{ fontSize: "0.73rem", color: "var(--text-muted)", lineHeight: 1.4 }}>
              {nextStep.text.length > 60 ? nextStep.text.slice(0, 58) + "…" : nextStep.text}
            </span>
          </div>
        )}
      </div>

      {/* Controls */}
      <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
        {phase !== "running" ? (
          <button
            className="ex-btn-primary"
            style={{ "--c": cat.color }}
            onClick={play}
            disabled={busy}
          >
            {stepIdx === 0 && tLeft === ex.steps[0].sec ? "▶  Begin" : "▶  Resume"}
          </button>
        ) : (
          <button className="ex-btn-outline" onClick={pause}>⏸  Pause</button>
        )}
        {stepIdx < ex.steps.length - 1 && (
          <button className="ex-btn-ghost" onClick={skip} disabled={busy}>
            Skip →
          </button>
        )}
      </div>

      {/* Keyboard hint */}
      <div style={{
        display: "flex", justifyContent: "center", alignItems: "center", gap: 6,
        opacity: 0.55, fontSize: "0.7rem", color: "var(--text-muted)",
      }}>
        <kbd style={{
          fontFamily: "monospace", fontSize: "0.65rem", fontWeight: 700,
          background: "var(--bg-secondary)", border: "1px solid var(--border-color)",
          borderBottom: "2px solid var(--border-color)",
          borderRadius: 5, padding: "1px 7px",
        }}>Space</kbd>
        <span>to {phase === "running" ? "pause" : "play"}</span>
      </div>

      {/* Science footnote */}
      <div className="ex-player-science">
        <span style={{ color: cat.color, flexShrink: 0 }}>🔬</span> {ex.science}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT (unchanged structure, minor polish)
// ─────────────────────────────────────────────────────────────────────────────
export default function Exercises() {
  const [mood] = useState(() => {
    const v = parseInt(localStorage.getItem(LS_MOOD));
    return isNaN(v) ? 50 : v;
  });

  const [history,  setHistory]  = useState(readHistory);
  const [streak]                = useState(() => parseInt(localStorage.getItem(LS_STREAK)) || 0);
  const [showDisc, setShowDisc] = useState(() => !localStorage.getItem(LS_DISCLAIMER));
  const [catId,    setCatId]    = useState(CATEGORIES[0].id);
  const [activeEx, setActiveEx] = useState(null);
  const [activeCat,setActiveCat]= useState(null);
  const [hovIdx,   setHovIdx]   = useState(null);

  const recs = getRecommendations(mood, history, streak, 3);
  const cat  = CATEGORIES.find(c => c.id === catId);

  function handleStart(ex, c) {
    const resolvedCat = c || CATEGORIES.find(cat => cat.id === ex.catId);
    setActiveEx(ex);
    setActiveCat(resolvedCat);
  }

  function handleComplete(exId, catId) {
    saveToHistory(exId, catId);
    setHistory(readHistory());
  }

  function dismissDisclaimer() {
    localStorage.setItem(LS_DISCLAIMER, "1");
    setShowDisc(false);
  }

  return (
    <>
      <style>{`
        /* ── original classes (all preserved) ── */
        .ex-cat-pill {
          display:flex;align-items:center;gap:6px;padding:7px 14px;border-radius:999px;
          font-size:0.81rem;font-weight:600;cursor:pointer;font-family:'Plus Jakarta Sans','DM Sans',sans-serif;
          white-space:nowrap;transition:all 0.2s ease;
        }
        .ex-cat-pill:focus{outline:none;}
        .ex-card {
          background:var(--card-bg-color);border:1.5px solid var(--border-color);
          border-radius:18px;overflow:hidden;display:flex;flex-direction:column;
          transition:transform 0.22s ease,box-shadow 0.22s ease,border-color 0.22s ease;
          cursor:pointer;
        }
        .ex-badge{border-radius:999px;padding:3px 10px;font-size:0.67rem;font-weight:700;letter-spacing:0.03em;}
        .ex-dur{
          font-size:0.7rem;color:var(--text-muted);font-weight:600;
          background:var(--bg-secondary);padding:3px 9px;border-radius:999px;
          border:1px solid var(--border-color);
        }
        .ex-btn-primary{
          background:var(--c,#6366f1);color:#fff;border:none;border-radius:10px;
          padding:10px 24px;font-weight:700;font-size:0.88rem;cursor:pointer;
          font-family:'Plus Jakarta Sans','DM Sans',sans-serif;
          box-shadow:0 4px 14px color-mix(in srgb,var(--c,#6366f1) 35%,transparent);
          transition:opacity 0.15s;
        }
        .ex-btn-primary:hover{opacity:0.87;}
        .ex-btn-primary:disabled{opacity:0.45;cursor:not-allowed;}
        .ex-btn-outline{
          background:transparent;color:var(--text-secondary-color);
          border:1.5px solid var(--border-color);
          border-radius:10px;padding:10px 22px;font-weight:600;font-size:0.88rem;
          cursor:pointer;font-family:'Plus Jakarta Sans','DM Sans',sans-serif;transition:all 0.15s;
        }
        .ex-btn-outline:hover{border-color:var(--text-muted);background:var(--bg-secondary);}
        .ex-btn-ghost{
          background:transparent;color:var(--text-muted);border:none;border-radius:10px;
          padding:10px 16px;font-weight:600;font-size:0.85rem;
          cursor:pointer;font-family:'Plus Jakarta Sans','DM Sans',sans-serif;transition:color 0.15s;
        }
        .ex-btn-ghost:hover{color:var(--text-secondary-color);}
        .ex-btn-ghost:disabled{opacity:0.35;cursor:not-allowed;}
        .ex-start-btn{
          width:100%;padding:10px 0;border-radius:10px;border:none;
          font-weight:700;font-size:0.875rem;cursor:pointer;
          font-family:'Plus Jakarta Sans','DM Sans',sans-serif;transition:all 0.2s ease;
        }
        .ex-science-box{
          display:flex;gap:5px;background:var(--bg-secondary);
          border:1px solid var(--border-subtle);border-radius:8px;
          padding:7px 9px;margin-bottom:12px;
        }
        .ex-science-text{font-size:0.69rem;color:var(--text-muted);line-height:1.4;}
        .ex-step-dot{width:4px;height:4px;border-radius:50%;margin-top:6px;flex-shrink:0;opacity:0.6;}
        .ex-step-text{font-size:0.73rem;color:var(--text-secondary-color);line-height:1.4;}
        .ex-more-steps{font-size:0.68rem;color:var(--text-muted);padding-left:10px;}
        .ex-disclaimer-box{
          display:flex;align-items:flex-start;gap:12px;
          background:var(--bg-secondary);
          border:1.5px solid var(--border-color);border-radius:14px;
          padding:14px 16px;margin-bottom:22px;
        }
        .ex-disclaimer-text{margin:0;flex:1;font-size:0.8rem;color:var(--text-secondary-color);line-height:1.65;}
        .ex-disclaimer-btn{
          background:none;border:none;cursor:pointer;
          color:var(--text-muted);font-size:1.1rem;padding:0 2px;flex-shrink:0;line-height:1;
          transition:color 0.15s;
        }
        .ex-disclaimer-btn:hover{color:var(--text-color);}
        .ex-player-step-text{
          margin:0;font-size:1rem;color:var(--text-color);font-weight:500;
          font-family:'Fraunces','DM Serif Display',Georgia,serif;line-height:1.55;text-align:center;
        }
        .ex-player-science{
          display:flex;gap:6px;background:var(--bg-secondary);
          border:1px solid var(--border-color);border-radius:10px;
          padding:10px 14px;font-size:0.73rem;color:var(--text-muted);line-height:1.5;
        }
        .ex-done-science{
          background:var(--bg-secondary);border:1px solid var(--border-color);
          border-radius:10px;padding:10px 14px;margin-bottom:20px;
          font-size:0.74rem;color:var(--text-muted);line-height:1.55;text-align:left;
        }
        .ex-done-stat{
          padding:12px 22px;background:var(--bg-secondary);
          border:1.5px solid var(--border-color);border-radius:14px;text-align:center;
        }
        .ex-done-stat-val{
          font-weight:800;font-size:1.4rem;font-family:'Fraunces','DM Serif Display',serif;
          color:var(--text-color);
        }
        .ex-done-stat-label{
          font-size:0.62rem;font-weight:700;text-transform:uppercase;
          color:var(--text-muted);letter-spacing:0.08em;
        }
        .ex-player-step-box{
          width:100%;background:var(--bg-secondary);border-radius:14px;
          padding:18px 22px;min-height:68px;display:flex;align-items:center;justify-content:center;
        }
        .ex-step-progress-bar{flex:1;border-radius:3px;}
        .ex-counter-label{font-size:0.58rem;font-weight:700;color:var(--text-muted);letter-spacing:0.12em;}
        .ex-card-label{font-size:0.7rem;font-weight:600;}
        .ex-section-label{
          font-size:0.7rem;font-weight:800;text-transform:uppercase;
          letter-spacing:0.1em;color:var(--text-muted);
        }
        .ex-divider-text{
          font-size:0.7rem;color:var(--text-muted);font-weight:700;white-space:nowrap;
        }
        .ex-step-counter{
          font-size:0.74rem;font-weight:700;color:var(--text-muted);
          background:var(--bg-secondary);border:1px solid var(--border-color);
          border-radius:8px;padding:4px 10px;
        }
        .ex-mood-bubble{
          width:34px;height:34px;border-radius:50%;display:flex;align-items:center;
          justify-content:center;font-size:1.1rem;transition:all 0.2s;
        }
        .ex-cat-count{border-radius:999px;padding:1px 7px;font-size:0.62rem;font-weight:700;}

        /* ── new animations for the improved player ── */
        @keyframes ep-pop {
          0%   { transform: scale(0.7); opacity: 0; }
          70%  { transform: scale(1.08); }
          100% { transform: scale(1);   opacity: 1; }
        }
        @keyframes ep-dots {
          0%,100% { opacity: 1; }
          50%      { opacity: 0.3; }
        }
      `}</style>

      <div style={{ fontFamily: "'DM Sans','Inter',sans-serif", padding: "4px 0 40px" }}>

        {/* Title */}
        <div style={{ marginBottom: 20 }}>
          <p style={{ margin: 0, fontSize: "0.875rem", color: "var(--text-muted,#64748b)" }}>
            Science-backed practices for body, mind &amp; focus · {ALL_EXERCISES.length} exercises across {CATEGORIES.length} categories
          </p>
        </div>

        {activeEx ? (
          <ExercisePlayer
            ex={activeEx}
            cat={activeCat}
            onBack={() => { setActiveEx(null); setActiveCat(null); }}
            onComplete={handleComplete}
          />
        ) : (
          <>
            {showDisc && <DisclaimerBanner onDismiss={dismissDisclaimer} />}

            <MoodContextPill mood={mood} />

            {/* Recommended */}
            <div style={{ marginBottom: 28 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12 }}>
                <span className="ex-section-label">✨ Picked for You</span>
                <span style={{ fontSize: "0.68rem", color: "var(--text-muted)", fontWeight: 400 }}>
                  — curated just for you
                </span>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(240px,1fr))", gap: 12 }}>
                {recs.map((ex, i) => (
                  <RecommendedCard key={ex.id} ex={ex} rank={i} onStart={handleStart} />
                ))}
              </div>
            </div>

            {/* Divider */}
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
              <div style={{ flex: 1, height: 1, background: "var(--border-color)" }} />
              <span className="ex-divider-text">Browse All</span>
              <div style={{ flex: 1, height: 1, background: "var(--border-color)" }} />
            </div>

            {/* Category pills */}
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 20 }}>
              {CATEGORIES.map(c => {
                const active = c.id === catId;
                return (
                  <button key={c.id} className="ex-cat-pill"
                    onClick={() => { setCatId(c.id); setHovIdx(null); }}
                    style={{
                      background: active ? c.color : "transparent",
                      color: active ? "#fff" : "var(--text-muted)",
                      border: `1.5px solid ${active ? c.color : "var(--border-color)"}`,
                      boxShadow: active ? `0 4px 14px ${c.color}40` : "none",
                    }}>
                    <span>{c.icon}</span>
                    <span>{c.label}</span>
                    <span className="ex-cat-count" style={{
                      background: active ? "rgba(255,255,255,0.22)" : "var(--bg-secondary)",
                      color: active ? "#fff" : "var(--text-muted)",
                    }}>{c.exercises.length}</span>
                  </button>
                );
              })}
            </div>

            {/* Section label */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: cat.color }} />
              <span style={{ color: cat.color, fontWeight: 700, fontSize: "0.77rem", textTransform: "uppercase", letterSpacing: "0.09em" }}>{cat.label}</span>
              <span style={{ color: "var(--text-muted)", fontSize: "0.77rem" }}>· {cat.exercises.length} exercises</span>
            </div>

            {/* Exercise grid */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(270px,1fr))", gap: 16 }}>
              {cat.exercises.map((ex, i) => {
                const int   = INTENSITY[ex.intensity] || INTENSITY.Gentle;
                const hov   = hovIdx === i;
                const isRec = recs.some(r => r.id === ex.id);
                return (
                  <div key={ex.id} className="ex-card"
                    style={{
                      transform: hov ? "translateY(-4px)" : "none",
                      boxShadow: hov ? `0 14px 36px ${cat.color}22,0 2px 8px rgba(0,0,0,0.08)` : "none",
                      borderColor: isRec ? cat.color + "90" : hov ? cat.color + "55" : "var(--border-color)",
                    }}
                    onMouseEnter={() => setHovIdx(i)}
                    onMouseLeave={() => setHovIdx(null)}
                    onClick={() => handleStart(ex, cat)}
                  >
                    {isRec && (
                      <div style={{ background: cat.color, color: "#fff", fontSize: "0.6rem", fontWeight: 800, padding: "4px 0", textAlign: "center", letterSpacing: "0.07em" }}>
                        ✨ PICKED FOR YOU
                      </div>
                    )}
                    <div style={{ padding: "16px 18px 12px", background: `linear-gradient(135deg,${cat.color}14,${cat.color}06)`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <span style={{ fontSize: "2rem" }}>{ex.emoji}</span>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
                        <span className="ex-badge" style={{ background: int.bg, color: int.fg }}>{ex.intensity}</span>
                        <span className="ex-dur">⏱ {ex.duration}</span>
                      </div>
                    </div>
                    <div style={{ padding: "0 18px 14px", flex: 1, display: "flex", flexDirection: "column" }}>
                      <h3 style={{ margin: "10px 0 5px", fontSize: "0.97rem", fontWeight: 700, color: "var(--text-color)", letterSpacing: "-0.01em" }}>{ex.label}</h3>
                      <p style={{ margin: "0 0 10px", fontSize: "0.8rem", color: "var(--text-muted)", lineHeight: 1.5 }}>{ex.desc}</p>
                      <div style={{ marginBottom: 10, flex: 1 }}>
                        {ex.steps.slice(0, 3).map((s, si) => (
                          <div key={si} style={{ display: "flex", alignItems: "flex-start", gap: 6, marginBottom: 4 }}>
                            <div className="ex-step-dot" style={{ background: cat.color }} />
                            <span className="ex-step-text">{s.text}</span>
                          </div>
                        ))}
                        {ex.steps.length > 3 && (
                          <span className="ex-more-steps">+{ex.steps.length - 3} more steps</span>
                        )}
                      </div>
                      <div className="ex-science-box">
                        <span style={{ color: cat.color, fontSize: "0.72rem", flexShrink: 0 }}>🔬</span>
                        <span className="ex-science-text">{ex.science}</span>
                      </div>
                      <button className="ex-start-btn"
                        style={{ background: hov ? cat.color : `${cat.color}18`, color: hov ? "#fff" : cat.color, boxShadow: hov ? `0 4px 14px ${cat.color}40` : "none" }}
                        onClick={e => { e.stopPropagation(); handleStart(ex, cat); }}>
                        Begin Exercise →
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </>
  );
}